import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Undo2, Wallet } from 'lucide-react';
import {
  settlementSummary, settlementPreview, postSettlement, listSettlements, fetchSettlement,
  reverseSettlement, releaseSettlementLine,
} from '../../api/resources';
import { inr, fmtDate } from '../../utils/format';
import { downloadPdf } from '../../utils/pdf';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/ui/PageHeader';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import Field from '../../components/ui/Field';
import DateInput from '../../components/ui/DateInput';
import Spinner from '../../components/ui/Spinner';
import { StatusBadge } from '../../components/ui/Badge';

const WORK_LABEL = { CUTTING: 'Cutting', STITCHING: 'Stitching' };
const MODE_LABEL = { DATE_RANGE: 'Date range', SELECTED: 'Selected items' };
const PAY_MODES = ['CASH', 'BANK', 'UPI', 'CHEQUE'];

const todayDdmmyyyy = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

// One key per settle dialog: a double-clicked or retried submit posts once.
const newKey = () => (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export default function Settlements() {
  const [tab, setTab] = useState('workers');
  const [settleFor, setSettleFor] = useState(null);
  const [detailId, setDetailId] = useState(null);

  return (
    <div className="space-y-4">
      <PageHeader icon={Wallet} title="Worker Payments" subtitle="Settle earned labour and review payout history" />

      <div className="flex rounded-lg border border-line bg-surface p-0.5 w-fit">
        {[['workers', 'Balances'], ['history', 'Settlement History']].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? 'bg-brand-700 text-white' : 'text-content-muted hover:text-content'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'workers' ? <Balances onSettle={setSettleFor} /> : <History onOpen={setDetailId} />}

      {settleFor && (
        <SettleDialog
          worker={settleFor}
          onClose={() => setSettleFor(null)}
          onPosted={(s) => { setSettleFor(null); setDetailId(s.id); }}
        />
      )}
      {detailId && <SettlementDetail id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

/* ------------------------------ Balances ------------------------------ */
function Balances({ onSettle }) {
  const { data, isLoading } = useQuery({ queryKey: ['settlement-summary'], queryFn: settlementSummary });
  const rows = data?.workers || [];
  const money = (v, cls = '') => <span className={`tabular-nums ${cls}`}>{inr(v)}</span>;

  const columns = [
    {
      key: 'worker',
      header: 'Worker',
      hideable: false,
      sortValue: (r) => r.worker.name,
      exportValue: (r) => r.worker.name,
      render: (r) => (
        <span>
          <span className="font-medium text-content">{r.worker.name}</span>
          {r.worker.status !== 'ACTIVE' && <span className="ml-1.5 text-2xs text-content-subtle">(inactive)</span>}
        </span>
      ),
    },
    { key: 'earned', header: 'Earned', align: 'right', sortValue: (r) => r.earned, exportValue: (r) => r.earned, render: (r) => money(r.earned) },
    { key: 'settled', header: 'Settled', align: 'right', sortValue: (r) => r.settled, exportValue: (r) => r.settled, render: (r) => money(r.settled, 'text-success-600') },
    {
      key: 'pending',
      header: 'Pending',
      align: 'right',
      sortValue: (r) => r.pending,
      exportValue: (r) => r.pending,
      render: (r) => (r.pending > 0
        ? <span className="font-semibold text-warning-700 tabular-nums">{inr(r.pending)} <span className="text-2xs font-normal text-content-subtle">({r.pendingCount})</span></span>
        : <span className="text-content-subtle">—</span>),
    },
    { key: 'inProgress', header: 'In Progress', align: 'right', sortValue: (r) => r.inProgress, exportValue: (r) => r.inProgress, render: (r) => money(r.inProgress, 'text-content-muted') },
    {
      key: 'last',
      header: 'Last Settled',
      sortValue: (r) => r.lastSettlement?.createdAt || '',
      exportValue: (r) => (r.lastSettlement ? `${r.lastSettlement.settlementNo} ${fmtDate(r.lastSettlement.createdAt)}` : ''),
      render: (r) => (r.lastSettlement
        ? <span className="text-xs"><b>{r.lastSettlement.settlementNo}</b> · {fmtDate(r.lastSettlement.createdAt)}</span>
        : <span className="text-content-subtle">Never</span>),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      hideable: false,
      align: 'right',
      exportValue: () => '',
      render: (r) => (
        <button className="btn-primary btn-sm" disabled={r.pending <= 0} onClick={() => onSettle(r.worker)}>
          Settle
        </button>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.worker.id}
      loading={isLoading}
      storageKey="settlement-balances"
      searchPlaceholder="Search worker…"
      searchKeys={['worker']}
      exportable
      exportFilename="worker-balances"
      emptyTitle="No worker activity yet"
      emptyHint="Assign workers to order items; completed work shows up here as pending."
    />
  );
}

/* ---------------------------- Settle dialog ---------------------------- */
function SettleDialog({ worker, onClose, onPosted }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [mode, setMode] = useState('DATE_RANGE');
  const [cutoff, setCutoff] = useState(todayDdmmyyyy());
  const [selected, setSelected] = useState(() => new Set());
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const keyRef = useRef(newKey());

  const { data, isFetching, isError, error: pvErr } = useQuery({
    queryKey: ['settle-preview', worker.id, mode, mode === 'DATE_RANGE' ? cutoff : ''],
    queryFn: () => settlementPreview({ workerId: worker.id, mode, ...(mode === 'DATE_RANGE' ? { to: cutoff } : {}) }),
    enabled: mode === 'SELECTED' || !!cutoff,
  });
  const items = data?.items || [];
  const chosen = mode === 'DATE_RANGE' ? items : items.filter((i) => selected.has(i.id));
  const total = Math.round(chosen.reduce((s, i) => s + i.cost, 0) * 100) / 100;

  // Group the list by order, as it is checked against order slips.
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      if (!m.has(it.invoiceId)) m.set(it.invoiceId, { invoiceId: it.invoiceId, orderNo: it.orderNo, customerName: it.customerName, rows: [] });
      m.get(it.invoiceId).rows.push(it);
    }
    return [...m.values()];
  }, [items]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));

  const post = useMutation({
    mutationFn: () => postSettlement({
      workerId: worker.id,
      mode,
      ...(mode === 'DATE_RANGE' ? { periodTo: cutoff } : { assignmentIds: [...selected] }),
      paymentMode,
      reference: reference.trim() || undefined,
      notes: notes.trim() || undefined,
      idempotencyKey: keyRef.current,
    }),
    onSuccess: (d) => {
      success(d.message);
      qc.invalidateQueries({ queryKey: ['settlement-summary'] });
      qc.invalidateQueries({ queryKey: ['settlements'] });
      onPosted(d.settlement);
    },
    onError: (e) => {
      error(e.response?.data?.message || 'Could not post the settlement.');
      // Something moved under us (e.g. settled elsewhere) — show the fresh list.
      qc.invalidateQueries({ queryKey: ['settle-preview', worker.id] });
      setSelected(new Set());
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Settle — ${worker.name}`}
      size="xl"
      footer={(
        <>
          <span className="mr-auto text-sm text-content-muted">
            {chosen.length} item(s) · <b className="text-lg text-brand-900 tabular-nums">{inr(total)}</b>
          </span>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!chosen.length || post.isPending || isFetching} onClick={() => post.mutate()}>
            {post.isPending ? 'Posting…' : `Settle ${inr(total)}`}
          </button>
        </>
      )}
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex rounded-lg border border-line bg-surface p-0.5">
          {Object.entries(MODE_LABEL).map(([k, label]) => (
            <button
              key={k}
              onClick={() => { setMode(k); setSelected(new Set()); }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${mode === k ? 'bg-brand-700 text-white' : 'text-content-muted hover:text-content'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {mode === 'DATE_RANGE' && (
          <Field label="Settle up to (inclusive)" className="w-48">
            <DateInput value={cutoff} onChange={setCutoff} />
          </Field>
        )}
      </div>

      {mode === 'DATE_RANGE' && (
        <p className="mb-3 text-xs text-content-subtle">
          {data?.lastSettlement
            ? <>Last settled {data.lastSettlement.settlementNo} up to {fmtDate(data.lastSettlement.periodTo || data.lastSettlement.createdAt)}. </>
            : <>No earlier settlement. </>}
          Includes all completed, unpaid work up to the cutoff — including anything older that was skipped before, so nothing is left behind.
        </p>
      )}

      {isFetching && <Spinner />}
      {isError && <p className="py-4 text-center text-sm text-danger-600">{pvErr?.response?.data?.message || 'Could not load the work to settle.'}</p>}
      {!isFetching && !isError && items.length === 0 && (
        <p className="rounded-lg bg-surface-sunken py-8 text-center text-sm text-content-subtle">Nothing completed and unpaid{mode === 'DATE_RANGE' ? ' up to this date' : ''}.</p>
      )}

      {!isFetching && items.length > 0 && (
        <div className="max-h-[45vh] overflow-auto rounded-lg border border-line">
          <table className="w-full">
            <thead className="sticky top-0">
              <tr>
                {mode === 'SELECTED' && (
                  <th className="th w-10">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-brand-600"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))}
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th className="th">Item</th>
                <th className="th">Work</th>
                <th className="th">Completed</th>
                <th className="th text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => [
                <tr key={`g-${g.invoiceId}`} className="bg-surface-sunken">
                  <td colSpan={mode === 'SELECTED' ? 5 : 4} className="px-4 py-1.5 text-xs font-semibold text-content-muted">
                    {g.orderNo} <span className="font-normal text-content-subtle">· {g.customerName || '—'}</span>
                  </td>
                </tr>,
                ...g.rows.map((it) => (
                  <tr key={it.id} className="row">
                    {mode === 'SELECTED' && (
                      <td className="td">
                        <input type="checkbox" className="h-3.5 w-3.5 accent-brand-600" checked={selected.has(it.id)} onChange={() => toggle(it.id)} aria-label={`Select ${it.itemName}`} />
                      </td>
                    )}
                    <td className="td">{it.itemName} × {it.qty}</td>
                    <td className="td text-content-muted">{WORK_LABEL[it.workType]}</td>
                    <td className="td text-xs">{fmtDate(it.completedAt)}</td>
                    <td className="td text-right tabular-nums">
                      {inr(it.cost)}
                      {it.overrideCost != null && (
                        <span className="ml-1 text-2xs text-warning-700" title={`Rate card: ${inr(it.calculatedCost)}`}>override</span>
                      )}
                    </td>
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Field label="Paid by">
          <select className="input" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
            {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Reference" hint="UTR / cheque no.">
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={190} />
        </Field>
        <Field label="Notes">
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={190} />
        </Field>
      </div>
    </Modal>
  );
}

/* ------------------------------- History ------------------------------- */
function History({ onOpen }) {
  const [status, setStatus] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data: summary } = useQuery({ queryKey: ['settlement-summary'], queryFn: settlementSummary });
  const workers = (summary?.workers || []).map((r) => r.worker);

  const params = {
    perPage: 200,
    ...(status ? { status } : {}),
    ...(workerId ? { workerId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
  const { data, isLoading } = useQuery({ queryKey: ['settlements', params], queryFn: () => listSettlements(params) });
  const rows = data?.settlements || [];

  const columns = [
    { key: 'settlementNo', header: 'No.', hideable: false, render: (s) => <span className="font-semibold text-brand-700">{s.settlementNo}</span> },
    { key: 'createdAt', header: 'Date', sortValue: (s) => s.createdAt, exportValue: (s) => fmtDate(s.createdAt), render: (s) => fmtDate(s.createdAt) },
    { key: 'worker', header: 'Worker', sortValue: (s) => s.worker?.name || '', exportValue: (s) => s.worker?.name || '', render: (s) => s.worker?.name || '—' },
    { key: 'mode', header: 'Mode', exportValue: (s) => MODE_LABEL[s.mode], render: (s) => <span className="text-xs text-content-muted">{MODE_LABEL[s.mode]}</span> },
    {
      key: 'period',
      header: 'Period',
      sortable: false,
      exportValue: (s) => `${s.periodFrom ? fmtDate(s.periodFrom) : ''} - ${s.periodTo ? fmtDate(s.periodTo) : ''}`,
      render: (s) => <span className="text-xs">{s.periodFrom ? fmtDate(s.periodFrom) : '…'} – {s.periodTo ? fmtDate(s.periodTo) : '…'}</span>,
    },
    { key: 'lineCount', header: 'Items', align: 'right' },
    { key: 'netAmount', header: 'Net Paid', align: 'right', sortValue: (s) => s.netAmount, exportValue: (s) => s.netAmount, render: (s) => <b className="tabular-nums">{inr(s.netAmount)}</b> },
    { key: 'status', header: 'Status', exportValue: (s) => s.status, render: (s) => <StatusBadge status={s.status} /> },
    { key: 'createdBy', header: 'By', defaultHidden: true, exportValue: (s) => s.createdBy || '', render: (s) => s.createdBy || '—' },
  ];

  return (
    <div className="space-y-3">
      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        storageKey="settlement-history"
        searchPlaceholder="Search settlement no. or worker…"
        searchKeys={['settlementNo', 'worker']}
        exportable
        exportFilename="settlements"
        onRowClick={(s) => onOpen(s.id)}
        emptyTitle="No settlements"
        emptyHint="Posted settlements appear here."
        toolbarExtra={(
          <div className="flex flex-wrap items-center gap-1.5">
            <select className="input input-sm w-36" value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
              <option value="">All workers</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <select className="input input-sm w-28" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              <option value="POSTED">Posted</option>
              <option value="REVERSED">Reversed</option>
            </select>
            <DateInput value={from} onChange={setFrom} className="input input-sm w-32" />
            <span className="text-2xs text-content-subtle">→</span>
            <DateInput value={to} onChange={setTo} className="input input-sm w-32" />
          </div>
        )}
      />
      {data && (
        <p className="text-right text-sm text-content-muted">
          Net paid in this view: <b className="text-brand-900 tabular-nums">{inr(data.totalNet)}</b>
          <span className="text-content-subtle"> (reversed settlements count as 0)</span>
        </p>
      )}
    </div>
  );
}

/* ---------------------------- Detail + audit ---------------------------- */
function SettlementDetail({ id, onClose }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const sheetRef = useRef(null);
  const [prompt, setPrompt] = useState(null); // { kind: 'reverse' } | { kind: 'release', line }
  const [reason, setReason] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['settlement', id], queryFn: () => fetchSettlement(id) });
  const s = data?.settlement;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['settlement', id] });
    qc.invalidateQueries({ queryKey: ['settlements'] });
    qc.invalidateQueries({ queryKey: ['settlement-summary'] });
  };

  const act = useMutation({
    mutationFn: () => (prompt.kind === 'reverse'
      ? reverseSettlement(id, reason)
      : releaseSettlementLine(id, prompt.line.id, reason)),
    onSuccess: (d) => { success(d.message); setPrompt(null); setReason(''); refresh(); },
    onError: (e) => error(e.response?.data?.message || 'Could not complete the action.'),
  });

  const posted = s?.status === 'POSTED';

  const savePdf = async () => {
    try {
      setPdfBusy(true);
      await downloadPdf(sheetRef.current, `${s.settlementNo}.pdf`);
    } catch (e) {
      error(e.message || 'Could not generate the PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={s ? `Settlement ${s.settlementNo}` : 'Settlement'}
      size="xl"
      footer={s && (
        <>
          {/* Awaited and caught: this used to be a bare call, so a failed
              capture rejected into nothing and the button looked dead. */}
          <button className="btn-outline mr-auto" disabled={pdfBusy} onClick={savePdf}>
            <Download size={14} /> {pdfBusy ? 'Preparing…' : 'PDF'}
          </button>
          {posted && (
            <button className="btn-danger" onClick={() => { setReason(''); setPrompt({ kind: 'reverse' }); }}>
              <Undo2 size={14} /> Reverse settlement
            </button>
          )}
          <button className="btn-primary" onClick={onClose}>Close</button>
        </>
      )}
    >
      {isLoading && <Spinner />}
      {s && (
        <div ref={sheetRef} className="doc-sheet rounded-lg p-1">
          <div className="grid gap-3 text-sm sm:grid-cols-4">
            <div><p className="text-xs text-content-subtle">Worker</p><b>{s.worker?.name}</b></div>
            <div><p className="text-xs text-content-subtle">Status</p><StatusBadge status={s.status} /></div>
            <div><p className="text-xs text-content-subtle">Mode</p>{MODE_LABEL[s.mode]}</div>
            <div><p className="text-xs text-content-subtle">Period</p>{s.periodFrom ? fmtDate(s.periodFrom) : '…'} – {s.periodTo ? fmtDate(s.periodTo) : '…'}</div>
            <div><p className="text-xs text-content-subtle">Posted</p>{fmtDate(s.createdAt)}{s.createdBy && ` by ${s.createdBy}`}</div>
            <div><p className="text-xs text-content-subtle">Paid by</p>{s.paymentMode || '—'}{s.reference && ` · ${s.reference}`}</div>
            {s.notes && <div className="sm:col-span-2"><p className="text-xs text-content-subtle">Notes</p>{s.notes}</div>}
          </div>

          {s.status === 'REVERSED' && (
            <p className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
              Reversed {fmtDate(s.reversedAt)}{s.reversedBy && ` by ${s.reversedBy}`} — {s.reversalReason}
            </p>
          )}

          <table className="mt-4 w-full">
            <thead>
              <tr>
                <th className="th">Item</th>
                <th className="th">Work</th>
                <th className="th">Completed</th>
                <th className="th text-right">Amount</th>
                <th className="th w-24" />
              </tr>
            </thead>
            <tbody>
              {s.orders.map((o) => [
                <tr key={`o-${o.invoiceId}`} className="bg-surface-sunken">
                  <td colSpan={3} className="px-4 py-1.5 text-xs font-semibold text-content-muted">{o.orderNo}</td>
                  <td className="px-4 py-1.5 text-right text-xs font-semibold tabular-nums">{inr(o.total)}</td>
                  <td />
                </tr>,
                ...o.lines.map((l) => (
                  <tr key={l.id} className="row">
                    <td className="td">{l.itemName} × {l.qty}</td>
                    <td className="td text-content-muted">{WORK_LABEL[l.workType]}</td>
                    <td className="td text-xs">{fmtDate(l.completedAt)}</td>
                    <td className={`td text-right tabular-nums ${l.status === 'RELEASED' ? 'text-content-subtle line-through' : ''}`}>{inr(l.amount)}</td>
                    <td className="td text-right">
                      {l.status === 'RELEASED' ? (
                        <span title={`${l.releaseReason || ''}${l.releasedBy ? ` — ${l.releasedBy}` : ''}`}><StatusBadge status="RELEASED" /></span>
                      ) : posted ? (
                        <button className="no-print text-xs font-semibold text-brand-700 hover:underline" onClick={() => { setReason(''); setPrompt({ kind: 'release', line: l }); }}>
                          Release
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )),
              ])}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-line text-sm">
                <td colSpan={3} className="px-4 py-2 text-right text-content-muted">Gross posted</td>
                <td className="px-4 py-2 text-right tabular-nums">{inr(s.grossAmount)}</td>
                <td />
              </tr>
              <tr className="text-base">
                <td colSpan={3} className="px-4 py-1 text-right font-semibold">Net payout</td>
                <td className="px-4 py-1 text-right font-bold text-brand-900 tabular-nums">{inr(s.netAmount)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Modal
        open={!!prompt}
        onClose={() => setPrompt(null)}
        title={prompt?.kind === 'reverse' ? 'Reverse this settlement?' : 'Release this line?'}
        size="sm"
        footer={(
          <>
            <button className="btn-outline" onClick={() => setPrompt(null)}>Cancel</button>
            <button className="btn-danger" disabled={reason.trim().length < 3 || act.isPending} onClick={() => act.mutate()}>
              {act.isPending ? 'Working…' : prompt?.kind === 'reverse' ? 'Reverse' : 'Release'}
            </button>
          </>
        )}
      >
        <p className="mb-3 text-sm text-content-muted">
          {prompt?.kind === 'reverse'
            ? 'Every item in this settlement becomes payable again. The settlement stays on record, marked reversed.'
            : `"${prompt?.line?.itemName}" becomes payable again and this settlement's net drops by ${inr(prompt?.line?.amount || 0)}.`}
        </p>
        <Field label="Reason" required hint="Recorded on the audit trail">
          <textarea className="textarea min-h-[70px]" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus maxLength={190} />
        </Field>
      </Modal>
    </Modal>
  );
}
